import { useAuth } from "./useAuth";
import { useState } from "react";
import { routes } from "@/lib/utils/routes";
import { AppError } from "@/lib/utils/errors";
import {
  validateEmail,
  validatePassword,
} from "@/modules/utils/auth/validators";
import { useGoogleLogin } from "@react-oauth/google";

export function useLoginForm(t: (key: string) => string) {
  const { login, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    isSaveLogin: false,
    terms: false,
  });
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    submit: "",
    terms: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isShowPassword, setIsShowPassword] = useState(false);

  const validateForm = (email: string, password: string) => {
    let isValid = true;
    const newErrors = {
      email: "",
      password: "",
      submit: "",
      terms: "",
    };

    if (!email) {
      newErrors.email = t("login.errors.emailRequired");
      isValid = false;
    } else if (!validateEmail(email)) {
      newErrors.email = t("login.errors.invalidEmail");
      isValid = false;
    }

    if (!password) {
      newErrors.password = t("login.errors.passwordRequired");
      isValid = false;
    } else if (!validatePassword(password)) {
      newErrors.password = t("login.errors.invalidPassword");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const onChangeEmail = (value: string) => {
    setFormData({ ...formData, email: value });
    validateForm(value, formData.password);
  };

  const onChangePassword = (value: string) => {
    setFormData({ ...formData, password: value });
    validateForm(formData.email, value);
  };

  const onToggleRememberMe = () => {
    setFormData({ ...formData, isSaveLogin: !formData.isSaveLogin });
  };

  const onToggleTerms = () => {
    setFormData((prev) => ({ ...prev, terms: !prev.terms }));
    setErrors((prev) => ({ ...prev, terms: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) {
      return;
    }
    setIsLoading(true);

    if (!validateForm(formData.email, formData.password)) {
      return;
    }

    try {
      await login(formData.email, formData.password, formData.isSaveLogin);
      window.location.href = routes.home;
    } catch (error) {
      const appErr = new AppError(error);
      appErr.logError("src/modules/auth/hooks/useLoginForm.ts handleSubmit");
      setErrors((prev) => ({
        ...prev,
        submit: t("login.errors.invalidCredentials"),
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const toggleShowPassword = () => {
    setIsShowPassword(!isShowPassword);
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setIsLoading(true);
        const userInfo = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        }).then(res => res.json());

        await login(
          userInfo.email,
          "",
          true,
          "google",
          userInfo.sub,
          userInfo.name
        );
        window.location.href = routes.home;
      } catch (error) {
        const appErr = new AppError(error);
        appErr.logError("useLoginForm.handleGoogleLogin");
        setErrors((prev) => ({
          ...prev,
          submit: t("login.errors.invalidCredentials"),
        }));
      } finally {
        setIsLoading(false);
      }
    },
    onError: () => {
      setErrors((prev) => ({
        ...prev,
        submit: t("login.errors.invalidCredentials"),
      }));
    }
  });

  const handleGoogleLogin = () => {
    if (!formData.terms) {
      setErrors((prev) => ({
        ...prev,
        terms: t("login.errors.termsRequired"),
      }));
      return;
    }
    setErrors((prev) => ({ ...prev, terms: "" }));
    googleLogin();
  };

  return {
    formData,
    errors,
    isLoading,
    isShowPassword,
    isAuthenticated,
    onChangeEmail,
    onChangePassword,
    onToggleRememberMe,
    onToggleTerms,
    handleSubmit,
    toggleShowPassword,
    handleGoogleLogin,
  };
}
